/**
 * Safe localStorage wrapper that prevents crashes in iframe/sandbox environments
 * where localStorage access throws SecurityError
 */

const memoryStorage: Map<string, string> = new Map();

function isStorageAvailable(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const testKey = '__storage_test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

// Lazy evaluation to avoid crashes during module load
let _storageAvailable: boolean | null = null;
function getStorageAvailable(): boolean {
  if (_storageAvailable === null) {
    _storageAvailable = isStorageAvailable();
  }
  return _storageAvailable;
}

export const safeStorage = {
  getItem(key: string): string | null {
    if (getStorageAvailable()) {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return memoryStorage.get(key) ?? null;
      }
    }
    return memoryStorage.get(key) ?? null;
  },

  setItem(key: string, value: string): void {
    if (getStorageAvailable()) {
      try {
        window.localStorage.setItem(key, value);
        return;
      } catch {
        // Fall through to memory storage
      }
    }
    memoryStorage.set(key, value);
  },

  removeItem(key: string): void {
    if (getStorageAvailable()) {
      try {
        window.localStorage.removeItem(key);
        return;
      } catch {
        // Fall through to memory storage
      }
    }
    memoryStorage.delete(key);
  },

  clear(): void {
    if (getStorageAvailable()) {
      try {
        window.localStorage.clear();
        return;
      } catch {
        // Fall through to memory storage
      }
    }
    memoryStorage.clear();
  },
};

/**
 * A storage adapter compatible with Supabase auth storage interface
 */
export const safeStorageAdapter = {
  getItem: (key: string): string | null => safeStorage.getItem(key),
  setItem: (key: string, value: string): void => safeStorage.setItem(key, value),
  removeItem: (key: string): void => safeStorage.removeItem(key),
};
